import { useEffect, useState, useCallback, Fragment } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { monthOptions, monthRangeFor } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, TrendingUp, TrendingDown, PiggyBank, CalendarClock, ChevronDown, ChevronUp } from "lucide-react";

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presetRange(preset) {
  const now = new Date();
  if (preset === "mes") return [isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)), isoLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
  if (preset === "mes_anterior") return [isoLocal(new Date(now.getFullYear(), now.getMonth() - 1, 1)), isoLocal(new Date(now.getFullYear(), now.getMonth(), 0))];
  if (preset === "3m") return [isoLocal(new Date(now.getFullYear(), now.getMonth() - 2, 1)), isoLocal(now)];
  if (preset === "ytd") return [isoLocal(new Date(now.getFullYear(), 0, 1)), isoLocal(now)];
  return null;
}

export default function CashFlow() {
  const { branchFilter } = useAuth();
  const [preset, setPreset] = useState("3m");
  const [group, setGroup] = useState("week");
  const [scope, setScope] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    let range = presetRange(preset);
    if (preset === "custom") {
      if (!custom.from || !custom.to) { setData(null); return; }
      range = [custom.from, custom.to];
    }
    const p = new URLSearchParams({ dfrom: range[0], dto: range[1], group, scope });
    if (branchFilter) p.set("branch_id", branchFilter);
    api.get(`/cashflow?${p}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [preset, custom, branchFilter, group, scope]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totales || {};

  return (
    <div className="space-y-6" data-testid="cashflow-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Cash Flow</h1>
          <p className="text-sm text-slate-500 mt-1">Flujo de caja real (banco + efectivo) con saldo acumulado y previsión de pagos</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Select value="" onValueChange={(ym) => { const [f, to] = monthRangeFor(ym); setPreset("custom"); setCustom({ from: f, to }); }}>
            <SelectTrigger data-testid="cashflow-month-filter" className="w-44 h-9"><SelectValue placeholder="Mes concreto..." /></SelectTrigger>
            <SelectContent>
              {monthOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger data-testid="cashflow-period-select" className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mes">Mes actual</SelectItem>
              <SelectItem value="mes_anterior">Mes anterior</SelectItem>
              <SelectItem value="3m">Últimos 3 meses</SelectItem>
              <SelectItem value="ytd">Año en curso</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger data-testid="cashflow-group-toggle" className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Por semana</SelectItem>
              <SelectItem value="month">Por mes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="cashflow-scope-toggle" className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Consolidado</SelectItem>
              <SelectItem value="banco">Solo banco/tarjeta</SelectItem>
              <SelectItem value="efectivo">Solo efectivo</SelectItem>
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <>
              <input data-testid="cashflow-date-from" type="date" value={custom.from} className="h-9 border rounded-md px-2 text-sm"
                onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
              <input data-testid="cashflow-date-to" type="date" value={custom.to} className="h-9 border rounded-md px-2 text-sm"
                onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
            </>
          )}
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-10 text-center">Cargando cash flow...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Saldo inicial", value: fmtEUR(data.saldo_inicial), icon: Wallet, testid: "cf-kpi-inicial" },
              { label: "Entradas", value: fmtEUR(t.ingresos ?? 0), icon: TrendingUp, testid: "cf-kpi-ingresos", color: "text-green-700" },
              { label: "Salidas", value: fmtEUR(t.gastos ?? 0), icon: TrendingDown, testid: "cf-kpi-gastos", color: "text-red-700" },
              { label: "Saldo final", value: fmtEUR(data.saldo_final), icon: PiggyBank, testid: "cf-kpi-final", color: (data.saldo_final ?? 0) >= 0 ? "text-green-700" : "text-red-700" },
            ].map((c) => (
              <Card key={c.testid} data-testid={c.testid}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{c.label}</CardTitle>
                  <c.icon className="w-4 h-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-extrabold font-num tracking-tight ${c.color || ""}`}>{c.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card data-testid="cf-table-card">
            <CardHeader><CardTitle className="text-base font-semibold font-heading">Flujo por {group === "week" ? "semana" : "mes"}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table data-testid="cf-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{group === "week" ? "Semana del" : "Mes"}</TableHead>
                    <TableHead className="text-right">Entradas</TableHead>
                    <TableHead className="text-right">Salidas</TableHead>
                    <TableHead className="text-right">Neto</TableHead>
                    <TableHead className="text-right">Saldo acumulado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.periodos.map((p) => (
                    <Fragment key={p.periodo}>
                      <TableRow data-testid={`cf-row-${p.periodo}`} className="cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpanded(expanded === p.periodo ? null : p.periodo)}>
                        <TableCell className="text-sm font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {expanded === p.periodo ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                            {group === "week" ? fmtDate(p.periodo) : p.periodo}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-num text-green-700">{fmtEUR(p.ingresos)}</TableCell>
                        <TableCell className="text-right font-num text-red-700">{p.gastos > 0 ? `−${fmtEUR(p.gastos)}` : fmtEUR(0)}</TableCell>
                        <TableCell className={`text-right font-num font-semibold ${p.neto >= 0 ? "text-green-700" : "text-red-700"}`}>{fmtEUR(p.neto)}</TableCell>
                        <TableCell className={`text-right font-num font-semibold ${p.saldo >= 0 ? "" : "text-red-700"}`}>{fmtEUR(p.saldo)}</TableCell>
                      </TableRow>
                      {expanded === p.periodo && (
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={5}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                              <div>
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Entradas</div>
                                {p.ingresos_detalle.map((d) => (
                                  <div key={d.label} className="flex justify-between text-sm py-0.5">
                                    <span className="text-slate-600">{d.label}</span>
                                    <span className="font-num">{fmtEUR(d.total)}</span>
                                  </div>
                                ))}
                                {p.ingresos_detalle.length === 0 && <div className="text-xs text-slate-400">Sin entradas</div>}
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Salidas</div>
                                {p.gastos_detalle.map((d) => (
                                  <div key={d.label} className="flex justify-between text-sm py-0.5">
                                    <span className="text-slate-600">{d.label}</span>
                                    <span className="font-num">{fmtEUR(d.total)}</span>
                                  </div>
                                ))}
                                {p.gastos_detalle.length === 0 && <div className="text-xs text-slate-400">Sin salidas</div>}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                  {data.periodos.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400 text-sm">Sin movimientos en el periodo.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card data-testid="cf-forecast-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-semibold font-heading">Previsión de pagos (próximos 60 días)</CardTitle>
              <CalendarClock className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent className="space-y-4">
              {data.prevision.length > 0 ? (
                <div className="flex gap-3 flex-wrap">
                  {data.prevision.map((p) => (
                    <div key={p.periodo} data-testid={`cf-prev-${p.periodo}`} className="border rounded-lg px-3 py-2 text-center">
                      <div className="text-xs text-slate-400">{group === "week" ? `sem. ${fmtDate(p.periodo)}` : p.periodo}</div>
                      <div className="font-num font-semibold text-amber-700">{fmtEUR(p.total)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">Sin pagos previstos en los próximos 60 días.</p>
              )}
              {data.prevision_sin_fecha > 0 && (
                <p className="text-xs text-slate-500">+ {fmtEUR(data.prevision_sin_fecha)} pendientes sin fecha de vencimiento.</p>
              )}
              {data.proximos_vencimientos.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Próximos vencimientos</div>
                  {data.proximos_vencimientos.map((v, i) => (
                    <div key={i} data-testid={`cf-venc-${i}`} className="flex justify-between text-sm py-1 border-b last:border-0">
                      <span className="text-slate-600 truncate">{v.supplier} <span className="text-xs text-slate-400">{v.invoice_number}</span></span>
                      <span className="text-xs text-slate-400 mx-3 whitespace-nowrap">{fmtDate(v.due_date)}</span>
                      <span className="font-num font-medium">{fmtEUR(v.pending)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
